import {Box, Button, HStack, Text} from '@chakra-ui/react'
import {useTranslation} from 'react-i18next'
import {useAppDispatch, useAppSelector} from '../../hooks/store'
import {
    isImpersonating,
    selectImpersonationTarget,
    stopImpersonation,
} from '../../slices/authSlice'
import {baseApi} from '../../services/apiService'

const formatName = (target: ReturnType<typeof selectImpersonationTarget>): string => {
    if (!target) return ''
    const parts = [target.firstName, target.lastName].filter(Boolean) as string[]
    return parts.length ? parts.join(' ') : target.email
}

// Top sticky affordance shown whenever an admin has an impersonation token
// loaded. Clicking "Stop" clears the in-memory JWT, invalidates RTK Query
// caches (so /me reloads as the admin), and sends the user back to /admin.
export default function ImpersonationBanner() {
    const {t} = useTranslation()
    const dispatch = useAppDispatch()
    const active = useAppSelector(isImpersonating)
    const target = useAppSelector(selectImpersonationTarget)

    if (!active || !target) return null

    const onStop = () => {
        dispatch(stopImpersonation())
        // Drop every cached request — they were fetched as the target user
        // and would mislead the admin UI until each query re-fired.
        dispatch(baseApi.util.resetApiState())
        window.location.replace('/admin/barber-shops')
    }

    const name = formatName(target)
    const shop = target.barberShopName

    return (
        <Box
            as="aside"
            position="sticky"
            top={0}
            zIndex={60}
            w="100%"
            bg="priority.50"
            borderBottom="2px solid"
            borderBottomColor="priority.500"
            px={{base: 4, md: 6}}
            py={3}
            role="status"
            aria-live="polite"
        >
            <HStack gap={3} justify="space-between" flexWrap="wrap">
                <HStack gap={3} minW={0}>
                    <Box
                        w="30px"
                        h="30px"
                        borderRadius="full"
                        bg="priority.100"
                        color="priority.600"
                        display="grid"
                        placeItems="center"
                        flexShrink={0}
                        aria-hidden
                    >
                        <Text fontSize="14px" lineHeight="1">⇄</Text>
                    </Box>
                    <Box minW={0}>
                        <Text fontSize="sm" fontWeight={700} color="neutral.900" lineHeight="1.2">
                            {t('admin.impersonation.banner.title', {name})}
                        </Text>
                        {shop && (
                            <Text fontSize="xs" color="priority.600" mt="2px" lineHeight="1.3">
                                {t('admin.impersonation.banner.subtitle', {shop})}
                            </Text>
                        )}
                    </Box>
                </HStack>
                <Button
                    size="sm"
                    bg="priority.500"
                    color="white"
                    _hover={{bg: 'priority.600'}}
                    onClick={onStop}
                    flexShrink={0}
                >
                    {t('admin.impersonation.banner.stop')}
                </Button>
            </HStack>
        </Box>
    )
}
